import { useTriggerLoad } from "@redotech/react-util/load";
import {
  FulfillmentOrderAddress,
  VerifiedAddress,
} from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { TextWeightValue } from "@redotech/redo-web/theme/typography";
import { memo, useContext, useRef } from "react";
import { EditAddressModalContext } from "./edit-address/edit-address-context";
import * as styles from "./order-fulfillment-address.module.css";

export enum FulfillmentAddressTheme {
  REGULAR = "regular",
  MEDIUM = "medium",
}

export const OrderFulfillmentAddress = memo(function OrderFulfillmentAddress({
  displayAddress,
  originalAddress,
  updateAddress,
  onChangeUseAutoCleanedAddress,
  hideEditButton,
  editButtonText = "Edit shipping address",
  titleText = "Shipping address",
  theme = FulfillmentAddressTheme.REGULAR,
  hideName = false,
  fontWeight,
}: {
  displayAddress: FulfillmentOrderAddress | VerifiedAddress | undefined | null;
  originalAddress?: FulfillmentOrderAddress | null;
  updateAddress?: (address: FulfillmentOrderAddress) => Promise<void>;
  onChangeUseAutoCleanedAddress?: (useAutoCleanedAddress: boolean) => void;
  hideEditButton?: boolean;
  editButtonText?: string;
  titleText?: string | null;
  theme?: FulfillmentAddressTheme;
  hideName?: boolean;
  fontWeight?: TextWeightValue;
}) {
  const openEditAddressModal = useContext(EditAddressModalContext);

  const addressRef = useRef<FulfillmentOrderAddress>();
  const [updateLoad, doUpdate] = useTriggerLoad(async () => {
    if (!addressRef.current || !updateAddress) {
      return;
    }
    await updateAddress(addressRef.current);
  });

  const showErrorBadges =
    displayAddress &&
    displayAddress.verified === false &&
    displayAddress.messages?.length;

  return (
    <Flex dir="column" gap="xs">
      {titleText && (
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {titleText}
        </Text>
      )}
      {displayAddress && (
        <Flex dir="column" fontWeight={theme} gap="none">
          {!hideName && displayAddress?.name && (
            <Text fontSize="xs" fontWeight={fontWeight} textColor="primary">
              {displayAddress.name}
            </Text>
          )}
          {displayAddress?.company && (
            <Text fontSize="xs" fontWeight={fontWeight} textColor="primary">
              {displayAddress.company}
            </Text>
          )}
          {displayAddress?.address1 && (
            <Text fontSize="xs" fontWeight={fontWeight} textColor="primary">
              {displayAddress.address1}
            </Text>
          )}
          {displayAddress?.address2 && (
            <Text fontSize="xs" fontWeight={fontWeight} textColor="primary">
              {displayAddress.address2}
            </Text>
          )}
          {(displayAddress?.city ||
            displayAddress?.province ||
            displayAddress?.zip) && (
            <Text fontSize="xs" fontWeight={fontWeight} textColor="primary">
              {displayAddress.city}
              {displayAddress.province && `, ${displayAddress.province}`}
            </Text>
          )}
          {displayAddress?.countryCode && (
            <Text fontSize="xs" fontWeight={fontWeight} textColor="primary">
              {displayAddress.zip}, {displayAddress.countryCode}
            </Text>
          )}
          {displayAddress?.phone && (
            <Text fontSize="xs" fontWeight={fontWeight} textColor="primary">
              {displayAddress.phone}
            </Text>
          )}
        </Flex>
      )}
      {!hideEditButton && (
        <Flex align="center">
          <Text
            className={styles.editButton}
            fontSize="xs"
            fontWeight="thin"
            hoverTextColor="primary"
            onClick={() => {
              if (!originalAddress) {
                return;
              }
              openEditAddressModal(
                originalAddress,
                async (newAddress) => {
                  addressRef.current = newAddress;
                  doUpdate();
                },
                (useAutoCleanedAddress) => {
                  onChangeUseAutoCleanedAddress?.(useAutoCleanedAddress);
                },
              );
            }}
            pt="xs"
            textColor="secondary"
          >
            {editButtonText}
          </Text>
          {updateLoad.pending ? <CircleSpinner /> : null}
        </Flex>
      )}
      {showErrorBadges && (
        <Flex dir="column" gap="xxs" pt="xs">
          {displayAddress?.messages?.map((message, index) => (
            <RedoBadge color="error" key={index} size="xs" text={message} />
          ))}
        </Flex>
      )}
    </Flex>
  );
});
